import { useState, useEffect } from 'react';
import { CortexDialog, CortexDialogBody } from '../dialogs/CortexDialog';
import { Eye as ViewIcon } from 'lucide-react';
import { Button, Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import type { ChipColor } from '@coordio/ui';
import { sessionService } from '../../services/sessionService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import { SessionStatus, MessageRole } from '../../types/session';
import type { AgentSessionDto, AgentMessageDto } from '../../types/session';

const statusLabels: Record<number, string> = {
  [SessionStatus.Active]: 'Active',
  [SessionStatus.Paused]: 'Paused',
  [SessionStatus.Completed]: 'Completed',
  [SessionStatus.Cancelled]: 'Cancelled',
  [SessionStatus.Expired]: 'Expired',
  [SessionStatus.Error]: 'Error',
};

const roleLabels: Record<number, string> = {
  [MessageRole.System]: 'System',
  [MessageRole.User]: 'User',
  [MessageRole.Assistant]: 'Assistant',
  [MessageRole.Tool]: 'Tool',
};

interface Props {
  agentId: string;
  agentSlug: string;
}

export const ResponsesTab = ({ agentId }: Props) => {
  const [sessions, setSessions] = useState<AgentSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<AgentSessionDto | null>(null);
  const [messages, setMessages] = useState<AgentMessageDto[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);

  useEffect(() => {
    loadSessions();
  }, [agentId]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const result = await sessionService.getSessions({
        agentDefinitionId: agentId,
        pageSize: 100,
        searchTerm: 'Responses',
      });
      setSessions(result.items ?? []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const viewMessages = async (session: AgentSessionDto) => {
    setSelectedSession(session);
    setMsgLoading(true);
    try {
      const msgs = await sessionService.getMessages(session.id);
      setMessages(msgs);
    } catch {
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  };

  const columns: DataTableColumn<AgentSessionDto>[] = [
    { id: 'sessionName', label: 'Session', render: (r) => <span className="text-sm truncate max-w-[280px] inline-block">{r.sessionName}</span> },
    { id: 'messageCount', label: 'Messages', render: (r) => r.messageCount ?? 0 },
    {
      id: 'status', label: 'Status',
      render: (r) => <Chip label={statusLabels[r.status] ?? String(r.status)} color={(r.status === SessionStatus.Active ? 'green' : 'default') as ChipColor} variant="outlined" />,
    },
    {
      id: 'createdAt', label: 'Created',
      render: (r) => new Date(r.createdAt).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="icon" onClick={() => viewMessages(r)}><ViewIcon size={16} /></Button>
            </TooltipTrigger>
            <TooltipContent>View messages</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
  ];

  return (
    <div>
      <p className="text-sm text-[var(--mui-palette-text-secondary)] mb-4">
        Sessions created via the Responses API for this agent.
      </p>
      <DataTable columns={columns} rows={sessions} loading={loading} emptyMessage="No Responses API sessions found." />

      <CortexDialog open={!!selectedSession} onClose={() => setSelectedSession(null)} title={<>Messages — {selectedSession?.sessionName}</>} size="md" data-testid="responses-messages-dialog">
        <CortexDialogBody>
          {msgLoading ? (
            <p>Loading...</p>
          ) : messages.length === 0 ? (
            <p className="text-[var(--mui-palette-text-secondary)]">No messages.</p>
          ) : (
            <div className="flex flex-col gap-3 mt-2">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className="p-4 rounded-lg border border-[var(--glass-border)]"
                  style={{
                    background: m.role === MessageRole.Assistant
                      ? 'var(--glass-bg-subtle)'
                      : 'transparent',
                  }}
                >
                  <span className="text-xs font-semibold uppercase text-[var(--mui-palette-primary-main)]">
                    {roleLabels[m.role] ?? m.role}
                  </span>
                  <p className="text-sm whitespace-pre-wrap mt-1">
                    {m.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CortexDialogBody>
      </CortexDialog>
    </div>
  );
};
